import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { DataMap } from '@grapecity/wijmo.grid'

import { BaseComponent, Bid, Grid, ListItem } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

interface CompetitorJobBidRow {
    id: string
    job: string
    heavyBidNum: number
    bidDate: Date
    bidAmount: number | null
    rank: number | null
    numBidders: number
    low: number
    high: number
    vanCon: number
    won: boolean
    lowerThanVanCon: boolean | null
    owner: string
    bidType: string
}

@Component({
    templateUrl: './competitor-job-bids.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
        WjInputModule,
    ],
})
export class CompetitorJobBidsComponent extends BaseComponent {
    grid: Grid<CompetitorJobBidRow>
    allBids: Bid[] = []
    competitorMap: ListItem<number>[] = []
    selectedCompetitor = -1
    jobTypeMap!: DataMap

    constructor() {
        super()
        this.grid = new Grid<CompetitorJobBidRow>(this.services, 'id', this)
            .wrapColumnHeaders()
            .filterBy(item => [item.job, item.heavyBidNum, item.owner, item.bidType])
            .isReadOnly(true)

        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.bid.queryAll(),
                    this.services.data.get('/api/VpOwner/GetCompetitors', new HttpParams().set('filterOnBids', true)),
                    this.services.data.jobTypes.queryAll(),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.allBids = result[0]
                        this.competitorMap = result[1].map(x => new ListItem(x.id, x.name))
                        this.jobTypeMap = new DataMap(result[2], 'id', 'fullName')
                        this.buildGridRows()
                    },
                }),
        )
    }

    onCompetitorChange() {
        this.buildGridRows()
    }

    buildGridRows() {
        if (this.selectedCompetitor < 0 || this.selectedCompetitor >= this.competitorMap.length) {
            this.grid.items = []
            return
        }

        const competitorId = this.competitorMap[this.selectedCompetitor].value

        const rows: CompetitorJobBidRow[] = []
        for (const bid of this.allBids) {
            if (!bid.competitorBids?.length) continue

            const competitorBid = bid.competitorBids.find(cb => cb.competitorId === competitorId)
            if (!competitorBid) continue

            const sortedBids = [...bid.competitorBids]
                .filter(cb => cb.bidAmount != null)
                .sort((a, b) => (a.bidAmount ?? Infinity) - (b.bidAmount ?? Infinity))

            const rankIndex = sortedBids.findIndex(cb => cb.competitorId === competitorId)

            rows.push({
                id: bid.id,
                job: bid.job,
                heavyBidNum: bid.heavyBidNum,
                bidDate: bid.bidDate,
                bidAmount: competitorBid.bidAmount ?? null,
                rank: rankIndex >= 0 ? rankIndex + 1 : null,
                numBidders: bid.competitorBids.length,
                low: bid.low,
                high: bid.high,
                vanCon: bid.vanCon,
                won: competitorBid.won === true,
                lowerThanVanCon: competitorBid.bidAmount != null && bid.vanCon ? competitorBid.bidAmount < bid.vanCon : null,
                owner: bid.ownerId,
                bidType: bid.bidTypeId,
            })
        }

        this.grid.items = rows
    }
}
